import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../App";
import { Menu, X, ChevronDown, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const Header = ({ onNavigate, onShowAnalyzer }) => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setIsLangOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navItems = [
    { key: "home", section: "hero" },
    { key: "ged", section: "ged" },
    { key: "solutions", section: "ged" },
    { key: "integrations", section: "integrations" },
    { key: "news", section: "news" },
    { key: "about", section: "about" },
  ];

  const languages = [
    { code: "fr", label: "FR", flag: "🇫🇷" },
    { code: "es", label: "ES", flag: "🇪🇸" },
    { code: "en", label: "EN", flag: "🇬🇧" },
  ];

  const currentLang = languages.find((l) => l.code === language) || languages[0];

  const subtitleLine1 = {
    fr: "Comparateur de logiciels GED",
    es: "Comparador de software GED",
    en: "DMS Software Comparison"
  };
  const subtitleLine2 = {
    fr: "pour entreprises",
    es: "para empresas",
    en: "for businesses"
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "header-solid" : "header-transparent"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Brand Unit: Logo + Title + Subtitle (single horizontal line) */}
          <div 
            className="flex items-center gap-2.5 cursor-pointer flex-shrink-0"
            onClick={() => onNavigate("hero")}
            data-testid="logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_7fe777d6-9bad-4a97-9808-a97a60a006ea/artifacts/x40hmr43_nuevo%20logo%20documatch.jpg" 
              alt="Documatch" 
              className="h-9 md:h-10 object-contain"
              style={{ background: "transparent" }}
              data-testid="header-logo-img"
            />
            {/* Mobile-only brand text (hidden on sm+ where the full brand block below shows) */}
            <span
              className={`sm:hidden font-bold tracking-tight text-[15px] leading-none flex items-center ${
                isScrolled ? "text-slate-900" : "text-white"
              }`}
              data-testid="mobile-brand-text"
            >
              Documatch
            </span>
            <div className="hidden sm:flex flex-col justify-center max-w-[160px] lg:max-w-none">
              <span className={`text-sm md:text-base font-bold tracking-tight leading-none ${
                isScrolled ? "text-slate-900" : "text-white"
              }`}>
                DOCUMATCH
              </span>
              <span className={`text-[9px] md:text-[11px] leading-tight mt-0.5 ${
                isScrolled ? "text-slate-500" : "text-slate-300"
              }`}>
                {subtitleLine1[language]}
              </span>
              <span className={`text-[9px] md:text-[11px] leading-tight ${
                isScrolled ? "text-slate-500" : "text-slate-300"
              }`}>
                {subtitleLine2[language]}
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-4" data-testid="desktop-nav">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={() => {
                  if (item.key === "analyzer") onShowAnalyzer();
                  else onNavigate(item.section);
                }}
                className={`text-sm font-medium transition-colors hover:text-blue-500 ${
                  isScrolled ? "text-slate-700" : "text-white/90"
                }`}
                data-testid={`nav-${item.key}`}
              >
                {t.nav[item.key]}
              </button>
            ))}
          </nav>

          {/* Language Dropdown & CTA & Mobile Menu */}
          <div className="flex items-center gap-4 md:gap-5">
            {/* Language Dropdown */}
            <div className="relative" ref={langRef} data-testid="language-dropdown">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                  isScrolled
                    ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    : "bg-white/15 text-white hover:bg-white/25"
                }`}
                data-testid="lang-selector-btn"
              >
                <Globe size={14} />
                {currentLang.label}
                <ChevronDown size={14} className={`transition-transform ${isLangOpen ? "rotate-180" : ""}`} />
              </button>
              
              <AnimatePresence>
                {isLangOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden min-w-[100px] z-50"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => { setLanguage(lang.code); setIsLangOpen(false); }}
                        className={`w-full px-4 py-2.5 text-left text-sm font-medium flex items-center gap-2 transition-colors ${
                          language === lang.code ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-50"
                        }`}
                        data-testid={`lang-${lang.code}`}
                      >
                        <span className="text-base">{lang.flag}</span>
                        {lang.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Desktop CTA */}
            <button
              onClick={onShowAnalyzer}
              className="hidden lg:inline-flex bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all shadow-md hover:shadow-lg"
              data-testid="header-cta"
            >
              {t.hero.cta}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? (
                <X size={24} className={isScrolled ? "text-slate-900" : "text-white"} />
              ) : (
                <Menu size={24} className={isScrolled ? "text-slate-900" : "text-white"} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white/95 backdrop-blur-lg border-t border-slate-200"
            data-testid="mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
              {navItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setTimeout(() => {
                      if (item.key === "analyzer") onShowAnalyzer();
                      else onNavigate(item.section);
                    }, 350);
                  }}
                  className="text-left py-3 px-4 text-slate-700 hover:bg-slate-50 rounded-lg font-medium"
                >
                  {t.nav[item.key]}
                </button>
              ))}
              <button
                onClick={() => { setIsMobileMenuOpen(false); setTimeout(() => onShowAnalyzer(), 350); }}
                className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-all"
                data-testid="mobile-cta"
              >
                {t.hero.cta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
